import { Box, Card, LinkBox, LinkOverlay } from "@chakra-ui/react"
import { isBlocksStep } from "@pathwright/blocks-engine/src/utils/utils"
import { useSuppressNavigationClickAfterDrag } from "@pathwright/utils/src/mouse/useSuppressNavigationClickAfterDrag"
import ResourceBackground from "@pathwright/ux/src/components/background/ResourceBackground"
import Link from "@pathwright/ux/src/components/link/Link"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { ComponentProps, useCallback, useMemo } from "react"
import {
  CoverPagePathItemFragment as CoverPagePreviewPathItem,
  PathItemType
} from "@pathwright/graphql-client/src/generated"
import Path from "../../path/legacy/path/Path"
import { RESOURCE_TYPE_COLLECTION } from "../../resource/constants"
import { useCoverPageContext } from "../CoverPageProvider"
import { useCoverPageTranslate } from "../utils"
import { PreviewPathProps } from "./types"
import PreviewCollectionPath from "./PreviewCollectionPath"

export const PreviewPathCard = ({
  data: pathItems,
  isImmersive,
  maxItemHeight
}: PreviewPathProps) => {
  const { t } = useCoverPageTranslate()
  const { resource, immersiveActive, getImmersiveRoutePath } =
    useCoverPageContext((v) => v)
  const isCollection = resource?.resource_type === RESOURCE_TYPE_COLLECTION

  const pathPreviewCardRoute = useMemo(() => {
    if (immersiveActive) return null
    const route = getImmersiveRoutePath?.({ name: "preview", id: "path" })
    return typeof route === "string" && route.length > 0 ? route : null
  }, [immersiveActive, getImmersiveRoutePath])

  const { onPointerDownCapture, onClickCapture } =
    useSuppressNavigationClickAfterDrag()

  const previewableSteps = useMemo(() => {
    return pathItems.reduce((acc, item) => {
      if (item.items) {
        acc.push(
          ...item.items.filter(
            (i) =>
              i.is_previewable &&
              i.type === PathItemType.Step &&
              isBlocksStep(i)
          )
        )
      }
      return acc
    }, [] as CoverPagePreviewPathItem[])
  }, [pathItems])

  const previewableIds = useMemo(
    () => new Set(previewableSteps.map((s) => Number(s.source_id))),
    [previewableSteps]
  )

  const expandedLessonIds = useMemo<number[]>(() => {
    const lessonIds = new Set<number>()
    pathItems.forEach((item) => {
      if (
        item.type === PathItemType.Lesson &&
        item.items?.some(
          (i) => i.is_previewable && i.type === PathItemType.Step
        )
      ) {
        lessonIds.add(Number(item.source_id))
      }
    })
    return Array.from(lessonIds)
  }, [pathItems])

  const previewablePaddingStyles = useMemo(() => {
    const styles: Record<string, any> = {}
    const ids = Array.from(previewableIds.values())
    ids.forEach((id) => {
      styles[`& .PathStep[data-source-id="${id}"]`] = {
        paddingRight: isImmersive ? "3rem" : "2rem"
      }
    })
    return styles
  }, [previewableIds, isImmersive])

  const renderStepChevron = useCallback(
    ({
      step,
      stepLinkProps
    }: {
      step: CoverPagePreviewPathItem
      stepLinkProps: Record<string, unknown>
    }) => {
      const stepId = Number(step.source_id)
      if (!previewableIds.has(stepId)) return null

      const { to, relativeTo, href } = stepLinkProps
      const hasRouteTarget = Boolean(to || relativeTo || href)

      return (
        <Box
          className="previewpath-chevron"
          position="absolute"
          right="1rem"
          top="50%"
          pointerEvents={isImmersive && hasRouteTarget ? "auto" : "none"}
          zIndex={1}
        >
          {isImmersive ? (
            hasRouteTarget ? (
              <LinkBox
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
              >
                <Link
                  asChild
                  {...(stepLinkProps as ComponentProps<typeof Link>)}
                  aria-label={t("immersive.nav.preview")}
                  onClick={(e) => {
                    const parentOnClick = stepLinkProps.onClick
                    if (typeof parentOnClick === "function") {
                      parentOnClick(e)
                    }
                    e.stopPropagation()
                  }}
                >
                  <LinkOverlay
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center"
                    minW="24px"
                    w="24px"
                    h="24px"
                    p={0}
                    borderRadius="full"
                    bgColor="brand.500"
                    color="white"
                    textDecoration="none"
                    _hover={{ bgColor: "brand.600" }}
                    _active={{ bgColor: "brand.700" }}
                  >
                    <Pathicon icon="chevron-right" size="0.9rem" />
                  </LinkOverlay>
                </Link>
              </LinkBox>
            ) : (
              <Box
                aria-label={t("immersive.nav.preview")}
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                minW="24px"
                w="24px"
                h="24px"
                p={0}
                borderRadius="full"
                bgColor="blackAlpha.200"
                color="whiteAlpha.700"
                cursor="not-allowed"
                pointerEvents="none"
              >
                <Pathicon icon="chevron-right" size="0.9rem" />
              </Box>
            )
          ) : (
            <Pathicon icon="chevron-right" color="#999" size="1rem" />
          )}
        </Box>
      )
    },
    [previewableIds, isImmersive, t]
  )

  return (
    <Box
      as={Card.Root}
      className="CoverPagePathPreview"
      position="relative"
      onPointerDownCapture={onPointerDownCapture}
      onClickCapture={onClickCapture}
      shadow={isImmersive ? "none" : "card"}
      borderRadius={isImmersive ? "none" : "card"}
      p={isImmersive || immersiveActive ? 0 : 4}
      bgColor={isImmersive ? "transparent" : "#F5F5F5"}
      h={isImmersive ? "100vh" : maxItemHeight}
      maxH={isImmersive ? "100%" : maxItemHeight}
      overflowY={isImmersive ? "auto" : "hidden"}
      border="none"
      css={{
        "& .ResourceBackground": {
          position: "absolute"
        },
        "& .previewpath-chevron": {
          transform: "translateY(-50%)",
          transition: "transform 120ms ease"
        },
        "& .PathStep:hover .previewpath-chevron": {
          transform: "translateY(-50%) translateX(3px)"
        },
        "& .chakra-card": {
          backgroundColor: "transparent"
        },
        "& .path-line": {
          display: "none"
        },
        "& .path-lesson": {
          margin: "0"
        },
        "& .chakra-link": {
          textDecoration: "none"
        },
        "& .LegacyPathView header > h4": {
          fontWeight: "700",
          fontSize: "28px",
          fontFamily:
            "var(--school-heading-font), 'ProximaNova-Regular', Helvetica, sans-serif"
        },
        "& .LegacyPathView header > p": {
          fontSize: "16px",
          fontWeight: "400",
          color: "#444"
        },
        "& .LegacyPathView": {
          padding: immersiveActive && !isImmersive ? "20px" : "5px"
        },
        ...previewablePaddingStyles
      }}
    >
      {!isImmersive && !immersiveActive && (
        <ResourceBackground resource={resource} forceBlur />
      )}
      {pathPreviewCardRoute ? (
        <Link
          to={pathPreviewCardRoute}
          aria-label={t("immersive.nav.preview", { defaultValue: "Preview" })}
          position="absolute"
          inset={0}
          zIndex={1}
          display="block"
          textDecoration="none"
          borderRadius="inherit"
          cursor="pointer"
        >
          <Box
            as="span"
            display="block"
            w="100%"
            h="100%"
            minH="100%"
            aria-hidden
          />
        </Link>
      ) : null}
      <Box
        className="CoverPagePathPreview__content"
        position="relative"
        zIndex={2}
        w="100%"
        minW={0}
        pointerEvents={pathPreviewCardRoute ? "none" : "auto"}
      >
        {isCollection ? (
          <PreviewCollectionPath items={pathItems} />
        ) : (
          <Path
            discreetToggleOnly
            items={pathItems}
            inverted
            renderStepChevron={renderStepChevron}
            getStepLinkProps={(source_id: number) => {
              if (previewableIds.has(Number(source_id))) {
                const to = getImmersiveRoutePath?.({
                  name: "preview",
                  id: source_id
                })
                return {
                  ...(typeof to === "string" && to.length > 0 ? { to } : {}),
                  onClick: (e: React.MouseEvent) => {
                    e.stopPropagation()
                  },
                  pointerEvents: "auto"
                }
              } else {
                return {
                  onClick: (e: React.MouseEvent) => {
                    e.stopPropagation()
                  },
                  cursor: "default"
                }
              }
            }}
            expandedLessonsIds={expandedLessonIds as unknown as never[]}
          />
        )}
      </Box>
    </Box>
  )
}
